import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Plus, Loader2, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogScrollArea,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { motion } from 'framer-motion';
import { ClaudeDesktopSuccessModalContent } from './ClaudeDesktopSuccessModal';
import { useTranslation } from 'react-i18next';
import { AdvancedSocketSelection } from '../AdvancedSocketSelection';
import { integrationRegistry } from '../../../integrations/registry';
import { SpaceSocket } from '../../../services/api';
import { getOrCreateDefaultSocket } from '../../../utils/socketUtils';
import { buildBindingPermissions } from '../shared/bindingPayload';
import { MemoryPermissionsFields } from '../shared/MemoryPermissionsFields';
import { ContentFiltersFields } from '../shared/ContentFiltersFields';

interface ClaudeDesktopIntegrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateIntegration: (integration: any) => Promise<any>;
  socket?: SpaceSocket;
  spaceId: string;
}

export default function ClaudeDesktopIntegrationModal({
  isOpen,
  onClose,
  onCreateIntegration,
  socket,
  spaceId
}: ClaudeDesktopIntegrationModalProps) {
  const { t } = useTranslation();
  const [integrationName, setIntegrationName] = useState('');
  const [description, setDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createdIntegration, setCreatedIntegration] = useState<any>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedSocket, setSelectedSocket] = useState<SpaceSocket | null>(socket || null);
  const [socketLoading, setSocketLoading] = useState(false);
  const [socketError, setSocketError] = useState<string | null>(null);

  // Get or create default socket when modal opens
  useEffect(() => {
    if (isOpen && !socket && spaceId) {
      const loadDefaultSocket = async () => {
        setSocketLoading(true);
        setSocketError(null);
        try {
          const defaultSocket = await getOrCreateDefaultSocket(spaceId, integrationRegistry.claude_desktop);
          setSelectedSocket(defaultSocket);
        } catch (error) {
          console.error('Failed to get or create default socket:', error);
          setSocketError(error instanceof Error ? error.message : 'Failed to load socket');
        } finally {
          setSocketLoading(false);
        }
      };
      loadDefaultSocket();
    } else if (socket) {
      setSelectedSocket(socket);
    }
  }, [isOpen, socket, spaceId]);
  
  // Permission states
  const [canRead, setCanRead] = useState(true);
  const [canWrite, setCanWrite] = useState(true);
  const [canSearch, setCanSearch] = useState(true);
  const [canDelete, setCanDelete] = useState(false);
  
  // Content filtering
  const [categories, setCategories] = useState('');
  const [excludeTags, setExcludeTags] = useState('');
  const [minImportance, setMinImportance] = useState(1);
  
  // Organization Features (simplified)
  const [enabledTools, setEnabledTools] = useState({
    categories: true,
    tags: true,
    importance: true
  });

  // Check template compatibility
  const socketTemplateId = selectedSocket?.socket_config?.template_id;
  const isCompatibleTemplate = !socketTemplateId || socketTemplateId === 'mcp' || socketTemplateId === 'api';
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!integrationName.trim() || !selectedSocket) return;

    // Validate template compatibility
    if (!isCompatibleTemplate) {
      alert(t('claudeDesktop.templateCompatibilityError', { ns: 'integrations' }) as string);
      return;
    }

    setIsCreating(true);
    try {
      const permissions = buildBindingPermissions({
        flags: {
          can_read: canRead,
          can_write: canWrite,
          can_search: canSearch,
          can_delete: canDelete,
        },
        categoriesComma: categories,
        excludeTagsComma: excludeTags,
        minImportance,
        includeRateLimit: false,
      });

      const auth_config = {
        description: description.trim() || undefined,
        tools: enabledTools,
      };

      const integrationData = {
        integration_type: 'claude_desktop',
        integration_name: integrationName.trim(),
        permissions,
        auth_config
      };

      // Use selectedSocket
      const createdIntegration = await onCreateIntegration({
        ...integrationData,
        socket_id: selectedSocket.socket_id
      });
      
      // Store the created integration and show success modal
      setCreatedIntegration({
        integration_name: integrationData.integration_name,
        binding_id: createdIntegration.binding_id,
        auth_config: {
          api_key: createdIntegration.auth_config.api_key, // Real API key from backend
          tools: integrationData.auth_config.tools
        },
        permissions: integrationData.permissions
      });
      setShowSuccessModal(true);
      
    } catch (error) {
      console.error('Failed to create Claude Desktop integration:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setCreatedIntegration(null);
    
    // Reset form
    setIntegrationName('');
    setDescription('');
    setCanRead(true);
    setCanWrite(true);
    setCanSearch(true);
    setCanDelete(false);
    setCategories('');
    setExcludeTags('');
    setMinImportance(1);
    setEnabledTools({
      categories: true,
      tags: true,
      importance: true
    });
    
    onClose();
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      if (showSuccessModal) {
        handleSuccessModalClose();
      } else {
        onClose();
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        variant="scrollable"
        className={showSuccessModal && createdIntegration ? 'max-w-2xl bg-white' : 'max-w-5xl bg-white'}
      >
        {showSuccessModal && createdIntegration ? (
          <ClaudeDesktopSuccessModalContent
            integration={createdIntegration}
            onClose={handleSuccessModalClose}
            spaceName={selectedSocket?.socket_name || 'Your Space'}
          />
        ) : (
          <>
            <DialogHeader className="flex-shrink-0 p-6 pb-4 pr-12">
              <DialogTitle className="text-xl font-semibold text-gray-900">Add Claude Integration</DialogTitle>
              <DialogDescription className="text-gray-600">
                {selectedSocket ? (
                  <>Create a new MCP integration for Claude Desktop and claude.ai (Connectors) on <span className="font-medium">{selectedSocket.socket_name}</span></>
                ) : (
                  'Setting up your integration...'
                )}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
              <DialogScrollArea className="space-y-6 px-6">
              {/* Integration Name */}
              <div className="space-y-2">
                <label className="text-gray-700 text-sm font-medium">
                  Integration Name *
                </label>
                <Input
                  value={integrationName}
                  onChange={(e) => setIntegrationName(e.target.value)}
                  placeholder="e.g., Personal Assistant, Work Claude, Research Helper"
                  className="bg-white border-gray-300 text-gray-900"
                  required
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <label className="text-gray-700 text-sm font-medium">
                  Description
                </label>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What will Claude use this memory integration for?"
                  className="bg-white border-gray-300 text-gray-900"
                />
              </div>

              {/* Claude Capabilities - Simplified unified section */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-gray-900 font-medium mb-2">Claude Capabilities</h3>
                  <p className="text-gray-600 text-sm mb-3">Choose what Claude can do with your memories</p>
                </div>

                <div className="space-y-3">
                  <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                    <MemoryPermissionsFields
                      variant="embedded"
                      layout="inline"
                      value={{
                        can_read: canRead,
                        can_write: canWrite,
                        can_search: canSearch,
                        can_delete: canDelete,
                      }}
                      onChange={(v) => {
                        setCanRead(v.can_read);
                        setCanWrite(v.can_write);
                        setCanSearch(v.can_search);
                        setCanDelete(v.can_delete);
                      }}
                    />
                  </div>

                  {/* Organization Features — auth_config.tools; not SpacePermissions */}
                  <div className="p-4 border border-gray-200 rounded-lg bg-blue-50">
                    <h4 className="text-gray-800 font-medium mb-3">Organization Features</h4>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={enabledTools.categories}
                          onChange={(e) =>
                            setEnabledTools((prev) => ({
                              ...prev,
                              categories: e.target.checked,
                            }))
                          }
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-gray-700">Organize by categories</span>
                      </label>

                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={enabledTools.tags}
                          onChange={(e) =>
                            setEnabledTools((prev) => ({ ...prev, tags: e.target.checked }))
                          }
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-gray-700">Use tags & labels</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4 border-t border-gray-200 pt-6">
                <ContentFiltersFields
                  categories={categories}
                  excludeTags={excludeTags}
                  minImportance={minImportance}
                  onCategoriesChange={setCategories}
                  onExcludeTagsChange={setExcludeTags}
                  onMinImportanceChange={setMinImportance}
                  showMinImportance={false}
                />
                <div className="space-y-2">
                  <label className="text-gray-700 text-sm font-medium">
                    Minimum Importance Level
                  </label>
                  <Select
                    value={minImportance.toString()}
                    onValueChange={(value) => setMinImportance(parseInt(value, 10))}
                  >
                    <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 - All memories</SelectItem>
                      <SelectItem value="2">2 - Low importance and above</SelectItem>
                      <SelectItem value="3">3 - Medium importance and above</SelectItem>
                      <SelectItem value="4">4 - Important and above</SelectItem>
                      <SelectItem value="5">5 - Very important only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Advanced Socket Selection */}
              {spaceId && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <AdvancedSocketSelection
                    integration={integrationRegistry.claude_desktop}
                    spaceId={spaceId}
                    currentSocket={selectedSocket}
                    onSocketChange={setSelectedSocket}
                    disabled={isCreating || socketLoading}
                  />
                  
                  {/* Socket Loading State */}
                  {socketLoading && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                        <p className="text-blue-800 text-sm">Setting up socket...</p>
                      </div>
                    </div>
                  )}

                  {/* Socket Error State */}
                  {socketError && !socketLoading && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
                        <div>
                          <p className="text-red-800 text-sm font-medium">Failed to load socket</p>
                          <p className="text-red-700 text-xs mt-1">{socketError}</p>
                          <p className="text-red-700 text-xs mt-1">You can create a socket manually in the advanced section above.</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Socket Info Display */}
                  {selectedSocket && !socketLoading && (
                    <>
                      {/* Template compatibility warning */}
                      {!isCompatibleTemplate && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                          <p className="text-amber-800 text-sm">
                            ⚠️ This socket uses the <strong>{socketTemplateId}</strong> template, which is not optimized for Claude Desktop integrations.
                            Consider creating a new socket with the <strong>MCP Integration</strong> template for the best experience.
                          </p>
                        </div>
                      )}
                      
                      {isCompatibleTemplate && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-3">
                          <p className="text-green-800 text-sm">
                            ✅ This socket uses the MCP Integration template, which is optimized for AI agent integrations like Claude Desktop.
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              </DialogScrollArea>
              <DialogFooter variant="scrollable" className="gap-3 px-6 pb-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!integrationName.trim() || isCreating || !selectedSocket || socketLoading}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white min-w-[140px]"
                >
                  {isCreating ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"
                      />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Integration
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
} 